import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import { useAccount } from '../../hooks/useAccount'
import TovExamplesSection from './TovExamplesSection'
import LanguagePreferenceSection from './LanguagePreferenceSection'
import CaptionPreferencesSection from './CaptionPreferencesSection'
import BrandProfileSection from './BrandProfileSection'
import BrandColorsSection from './BrandColorsSection'
import PostIntervalSection from './PostIntervalSection'
import FontPreferenceSection from './FontPreferenceSection'
import QuietHoursSection from './QuietHoursSection'
import AiTrainingSection from './AiTrainingSection'

// Toast notification component
function Toast({ message, type = 'info', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  const typeStyles = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500'
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
      <div className={`${typeStyles[type]} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}>
        <span>{message}</span>
        <button
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

function BrandSettingsPage() {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()
  const { accountId } = useParams()
  const { selectedAccount, accounts } = useAccount()

  // Use accountId from URL params or fall back to selected account
  const effectiveAccountId = accountId || selectedAccount?.id

  // Find the account from the accounts list
  const account = accounts.find(acc => acc.id === effectiveAccountId)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [activeTab, setActiveTab] = useState('sprache')

  // Brand settings state
  const [tovExamples, setTovExamples] = useState([])
  const [language, setLanguage] = useState('de')
  const [emojiPreference, setEmojiPreference] = useState('sparse')
  const [hashtagPreference, setHashtagPreference] = useState('minimal')
  const [textColor, setTextColor] = useState('#FFFFFF')
  const [backgroundColor, setBackgroundColor] = useState('#000000')
  const [useBackground, setUseBackground] = useState(false)
  const [timezone, setTimezone] = useState('UTC')
  const [postIntervalHours, setPostIntervalHours] = useState(24)
  const [defaultFont, setDefaultFont] = useState('Classic')
  const [quietHours, setQuietHours] = useState([])
  const [aiTrainingOptOut, setAiTrainingOptOut] = useState(false)

  // Track if settings have changed
  const [hasChanges, setHasChanges] = useState(false)

  // Redirect if no account selected
  useEffect(() => {
    if (!loading && !effectiveAccountId) {
      navigate('/accounts')
    }
  }, [loading, effectiveAccountId, navigate])

  // Sync URL with selected account when user switches via AccountSelector
  // This ensures the URL reflects the currently selected account
  useEffect(() => {
    if (accountId && selectedAccount?.id && accountId !== selectedAccount.id) {
      navigate(`/accounts/${selectedAccount.id}/brand-settings`, { replace: true })
    }
  }, [accountId, selectedAccount?.id, navigate])

  // Load brand settings on mount
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadSettings = async () => {
      if (!effectiveAccountId) {
        if (isMounted) {
          setLoading(false)
        }
        return
      }

      try {
        if (isMounted) {
          setLoading(true)
        }
        const settings = await api.getBrandSettings(effectiveAccountId, { signal: abortController.signal })

        if (isMounted) {
          setTovExamples(settings.tov_examples || [])
          setLanguage(settings.language || 'de')
          setEmojiPreference(settings.emoji_preference || 'sparse')
          setHashtagPreference(settings.hashtag_preference || 'minimal')
          setTextColor(settings.text_color || '#FFFFFF')
          setBackgroundColor(settings.background_color || '#000000')
          setUseBackground(settings.use_background || false)
          setTimezone(settings.timezone || 'UTC')
          setPostIntervalHours(settings.post_interval_hours || 24)
          setDefaultFont(settings.default_font || 'Classic')
          const schedule = settings.posting_schedule || { quiet_hours: [] }
          setQuietHours(schedule.quiet_hours || [])
          setAiTrainingOptOut(settings.ai_training_opt_out || false)
          setHasChanges(false)
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('Failed to load brand settings:', error)
          setToast({
            message: t('brand.messages.loadError', { error: error.message }),
            type: 'error'
          })
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadSettings()

    return () => {
      isMounted = false;
      abortController.abort();
    }
  }, [effectiveAccountId, t])

  // Mark as changed when settings are modified
  const handleTovExamplesChange = useCallback((examples) => {
    setTovExamples(examples)
    setHasChanges(true)
  }, [])

  const handleTextColorChange = useCallback((color) => {
    setTextColor(color)
    setHasChanges(true)
  }, [])

  const handleBackgroundColorChange = useCallback((color) => {
    setBackgroundColor(color)
    setHasChanges(true)
  }, [])

  const handleUseBackgroundChange = useCallback((value) => {
    setUseBackground(value)
    setHasChanges(true)
  }, [])

  const handleTimezoneChange = useCallback((value) => {
    setTimezone(value)
    setHasChanges(true)
  }, [])

  const handlePostIntervalChange = useCallback((hours) => {
    setPostIntervalHours(hours)
    setHasChanges(true)
  }, [])

  // Language auto-saves independently, no dirty state
  const handleLanguageAutoSave = useCallback((lang) => {
    setLanguage(lang)
  }, [])

  const handleEmojiPreferenceChange = useCallback((pref) => {
    setEmojiPreference(pref)
    setHasChanges(true)
  }, [])

  const handleHashtagPreferenceChange = useCallback((pref) => {
    setHashtagPreference(pref)
    setHasChanges(true)
  }, [])

  const handleFontChange = useCallback((fontId) => {
    setDefaultFont(fontId)
    setHasChanges(true)
  }, [])

  const handleQuietHoursChange = useCallback((hours) => {
    setQuietHours(hours)
    setHasChanges(true)
  }, [])

  // Validation - navigates to the affected tab on error
  const validateSettings = () => {
    // Validate TOV examples only if provided (optional)
    if (tovExamples.length > 10) {
      setActiveTab('identitaet')
      setToast({ message: t('validation.tovMaxExceeded'), type: 'error' })
      return false
    }

    // Validate each example if any exist
    for (let i = 0; i < tovExamples.length; i++) {
      const example = tovExamples[i]
      if (!example.text || example.text.length < 20 || example.text.length > 280) {
        setActiveTab('identitaet')
        setToast({ message: t('validation.tovLengthInvalid', { number: i + 1 }), type: 'error' })
        return false
      }
    }

    // Validate colors (design tab)
    const hexPattern = /^#[0-9A-Fa-f]{6}$/
    if (!hexPattern.test(textColor)) {
      setActiveTab('design')
      setToast({ message: t('validation.textColorInvalid'), type: 'error' })
      return false
    }
    if (!hexPattern.test(backgroundColor)) {
      setActiveTab('design')
      setToast({ message: t('validation.backgroundColorInvalid'), type: 'error' })
      return false
    }

    return true
  }

  // Save settings
  const handleSave = async () => {
    if (!validateSettings()) {
      return
    }

    try {
      setSaving(true)

      await api.updateBrandSettings(effectiveAccountId, {
        tov_examples: tovExamples,
        language: language,
        emoji_preference: emojiPreference,
        hashtag_preference: hashtagPreference,
        text_color: textColor,
        background_color: backgroundColor,
        use_background: useBackground,
        timezone: timezone,
        post_interval_hours: postIntervalHours,
        default_font: defaultFont,
        posting_schedule: {
          quiet_hours: quietHours
        }
      })

      setHasChanges(false)
      setToast({
        message: t('brand.messages.saved'),
        type: 'success'
      })
    } catch (error) {
      console.error('Failed to save brand settings:', error)
      // Extract server error message
      let errorMessage = error.message
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }
      setToast({
        message: errorMessage || t('brand.messages.saveError'),
        type: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  const tabs = [
    { id: 'sprache', label: t('brand.tabs.sprache', 'Sprache') },
    { id: 'identitaet', label: t('brand.tabs.identitaet', 'Identität') },
    { id: 'design', label: t('brand.tabs.design', 'Design') },
    { id: 'zeitplanung', label: t('brand.tabs.zeitplanung', 'Zeitplanung') },
  ]

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // No account selected
  if (!effectiveAccountId || !account) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('brand.noAccount.title')}</h2>
          <p className="text-gray-600">
            {t('brand.noAccount.message')}
          </p>
          <button
            onClick={() => navigate('/accounts')}
            className="px-6 py-2 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            {t('brand.noAccount.button')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('brand.title')}</h1>
        <p className="mt-2 text-gray-600">
          {t('brand.subtitle', { username: account.username || 'this account' })}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-8">
        {activeTab === 'sprache' && (
          <div className="card">
            <LanguagePreferenceSection
              language={language}
              accountId={effectiveAccountId}
              onLanguageChange={handleLanguageAutoSave}
            />
          </div>
        )}

        {activeTab === 'identitaet' && (
          <>
            <div className="card">
              <BrandProfileSection
                accountId={effectiveAccountId}
                onProfileUpdate={() => setHasChanges(true)}
              />
            </div>
            <div className="card">
              <TovExamplesSection
                examples={tovExamples}
                onChange={handleTovExamplesChange}
              />
            </div>
            <div className="card">
              <CaptionPreferencesSection
                emojiPreference={emojiPreference}
                hashtagPreference={hashtagPreference}
                onEmojiPreferenceChange={handleEmojiPreferenceChange}
                onHashtagPreferenceChange={handleHashtagPreferenceChange}
              />
            </div>
          </>
        )}

        {activeTab === 'design' && (
          <>
            <div className="card">
              <BrandColorsSection
                textColor={textColor}
                backgroundColor={backgroundColor}
                useBackground={useBackground}
                onTextColorChange={handleTextColorChange}
                onBackgroundColorChange={handleBackgroundColorChange}
                onUseBackgroundChange={handleUseBackgroundChange}
              />
            </div>
            <div className="card">
              <FontPreferenceSection
                defaultFont={defaultFont}
                onFontChange={handleFontChange}
              />
            </div>
          </>
        )}

        {activeTab === 'zeitplanung' && (
          <>
            <div className="card">
              <PostIntervalSection
                postIntervalHours={postIntervalHours}
                timezone={timezone}
                onIntervalChange={handlePostIntervalChange}
                onTimezoneChange={handleTimezoneChange}
              />
            </div>
            <div className="card">
              <QuietHoursSection
                quietHours={quietHours}
                timezone={timezone}
                onChange={handleQuietHoursChange}
              />
            </div>
            <div className="card">
              <AiTrainingSection
                aiTrainingOptOut={aiTrainingOptOut}
                accountId={effectiveAccountId}
              />
            </div>
          </>
        )}
      </div>

      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-end gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 text-white bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('brand.button.saving') : t('brand.button.save')}
            </button>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default BrandSettingsPage
